import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { UseMutationOptions, useMutation } from 'react-query'
import { SuggestionRequest } from 'types'
import { AIInsufficientCreditsError } from 'api/clients/AiClient'
import type { RegenerateVariables } from 'hooks/queries/designs/useRegenerate'

type Props = {
  videoId: string
}

type Data = {
  request: SuggestionRequest
}

type Options = Omit<UseMutationOptions<Data, unknown, RegenerateVariables>, 'mutationKey' | 'mutationFn'>

const regenerate = async (workspaceId: number, videoId: string, variables: RegenerateVariables) => {
  const res = await sendToApi(`/w/${workspaceId}/videos/${videoId}/regenerate`, {
    method: 'POST',
    body: {
      media_file_key: variables.media_file_key,
      topic: variables.topic,
      language: variables.language,
      media_source_override: variables.media_source_override,
      avatar_id: variables.avatar_id,
      avatar_angle: variables.avatar_angle,
      avatar_script: variables.avatar_script,
      avatar_captions: variables.avatar_captions,
      avatar_caption_style: variables.avatar_caption_style,
      video_script: variables.video_script,
      video_caption_style: variables.video_caption_style,
      video_voiceover_voice: variables.video_voiceover_voice,
    },
  })

  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    if (res.type === 'error' && res.status === 402) {
      throw new AIInsufficientCreditsError(
        (res.data as { error?: string })?.error || 'Insufficient credits for video regeneration.',
      )
    }
    throw new Error(res.data)
  }

  return res.data
}

export const useVideoRegenerate = ({ videoId }: Props, options?: Options) => {
  const workspaceId = useCurrentWorkspaceId()
  return useMutation<Data, Error, RegenerateVariables>((vars) => regenerate(workspaceId, videoId, vars), options)
}
